import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import colours from "../../lib/colours";
import mapConfig from "../../lib/mapConfig";
import {
  LegendItem,
  isLegendItemVisible,
  isLegendItemVisibleAtScale,
} from "../../utils/mapUtils";
import CloseButton from "../atoms/CloseButton";
import { FilledButtonAlt } from "../atoms/FilledButton";
import { ReactComponent as IdentifyIcon } from "../../assets/svg/identify.svg";
import Callout from "../molecules/Callout";

const StyledIdentifyContainer = styled.div`
  position: absolute;
  min-width: 32px;
  max-width: 90%;
  min-height: 32px;
  right: 15px;
  background-color: rgba(255, 255, 255, 1);
  z-index: 3;
  color: ${colours.white};
  box-shadow: 0px 0px 4px 0px rgb(0 0 0 / 25%);
  overflow: auto;
  display: flex;
  flex-direction: column;
  top: 114px;
  @media (max-width: 1400px) {
    top: 168px;
  }
`;

const StyledIdentifyButton = styled(FilledButtonAlt)`
  border-radius: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeadingContainer = styled.div`
  text-align: left;
  padding: 10px 8px 8px 20px;
  max-width: 342px;
  color: ${colours.th1};
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
`;

const StyledHeading = styled.h3`
  font-weight: 400;
  font-size: 16px;
  color: ${colours.th1};
  margin-block: 0;
  margin-inline: 8px;
  text-transform: uppercase;
  flex: 1;
`;

const StyledContainer = styled.div`
  text-align: left;
  padding-right: 16px;
  padding-left: 20px;
  padding-bottom: 16px;
  box-sizing: border-box;
  max-width: 358px;
  min-width: 324px;
  color: ${colours.th1};
  overflow: auto;
`;

const StyledInfoContainer = styled.div`
  max-width: 324px;
`;

const StyledCloseButtonContainer = styled.div``;

const StyledIconContainer = styled.div`
  position: relative;
  top: 2px;
`;

const StyledSelectLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: ${colours.th1};
  display: block;
  margin-block: 8px 4px;
`;

const StyledSelect = styled.select`
  height: 36px;
  width: 100%;
  font-size: 16px;
  font-family: "vic";
  font-weight: 500;
  background-color: ${colours.white};
  border: 1px solid ${colours.grey1};
  border-radius: 4px;
  padding-right: 20px;
  padding-left: 4px;
`;

const iconStyle = {
  height: "20px",
  width: "14px",
  color: colours.th1,
};

interface IProps {
  map: any;
  legendItems: LegendItem[];
  identifiableLayer: LegendItem | undefined;
  setIdentifiableLayer: any;
  mapScale: any;
  open: boolean;
  setOpen: any;
  setClosed: any;
}

function IdentifyButton({
  map,
  legendItems,
  identifiableLayer,
  setIdentifiableLayer,
  mapScale,
  open,
  setOpen,
  setClosed,
}: IProps) {
  const identifiableLayers = useMemo(() => {
    const result: LegendItem[] = [];
    if (!open || !map || !map.layers.items[0]) return result;
    mapConfig.mapServiceConfig.identifiableLayers.forEach((x: number) => {
      if (isLegendItemVisible(x, map)) {
        const legendItem = legendItems.filter((y) => y.layerId === x)[0];
        if (isLegendItemVisibleAtScale(legendItem, mapScale)) {
          result.push(legendItem);
        }
      }
    });
    return result;
  }, [open, legendItems, map, mapScale]);

  const handleClose = () => {
    setClosed();
  };
  const handleOpen = () => {
    setOpen();
  };

  const onLayerChange = (e: any) => {
    const legendItem = legendItems.filter(
      (x) => x.layerId === Number(e.target.value)
    )[0];
    setIdentifiableLayer(legendItem);
  };

  // set the identifiable layer to something valid if needed
  useEffect(() => {
    if (!open) return;
    if (
      !identifiableLayers
        .map((x) => x.layerId)
        .includes((identifiableLayer as LegendItem)?.layerId)
    ) {
      setIdentifiableLayer(identifiableLayers[0]);
    }
  }, [identifiableLayers, identifiableLayer, open, setIdentifiableLayer]);

  const renderNoLayersInfo = () => {
    return (
      <StyledInfoContainer>
        <Callout type="info">
          No visible identifiable layers.
          <br />
          Try activating some layers from the layer control and zooming in to a
          visible scale if needed.
        </Callout>
      </StyledInfoContainer>
    );
  };

  const renderLayerSelect = () => {
    return (
      <StyledContainer>
        <StyledSelectLabel htmlFor="identify_layer_select">
          Layer
        </StyledSelectLabel>
        <StyledSelect
          id="identify_layer_select"
          value={identifiableLayer?.layerId}
          onChange={onLayerChange}
        >
          {identifiableLayers.map((x) => (
            <option key={x.layerId} value={x.layerId}>
              {x.layerName}
            </option>
          ))}
        </StyledSelect>
      </StyledContainer>
    );
  };

  const renderWindow = () => {
    return (
      <StyledIdentifyContainer>
        <StyledHeadingContainer>
          <StyledIconContainer>
            <IdentifyIcon style={iconStyle} />
          </StyledIconContainer>
          <StyledHeading>Identify features</StyledHeading>
          <StyledCloseButtonContainer>
            <CloseButton fontSize={"18px"} onClick={handleClose} />
          </StyledCloseButtonContainer>
        </StyledHeadingContainer>
        {identifiableLayers.length ? renderLayerSelect() : renderNoLayersInfo()}
      </StyledIdentifyContainer>
    );
  };

  const renderButton = () => {
    return (
      <StyledIdentifyContainer>
        <StyledIdentifyButton onClick={handleOpen}>
          <IdentifyIcon style={iconStyle} />
        </StyledIdentifyButton>
      </StyledIdentifyContainer>
    );
  };

  return open ? renderWindow() : renderButton();
}

export default IdentifyButton;
